// src/pages/AuditLogs/components/AuditLogsFilters.tsx
// Filters component for searching and filtering audit logs

import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Flatpickr from 'react-flatpickr';
import { Vietnamese } from 'flatpickr/dist/l10n/vn.js';
import { setFilters, clearFilters } from 'slices/auditLogs/reducer';
import { fetchAuditLogModules } from 'slices/auditLogs/thunk';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { ACTION_TYPE_OPTIONS } from 'pages/AuditLogs/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('audit_logs');

interface AuditLogsFiltersProps {
  onDashboard: () => void;
  onExport: () => void;
}

const AuditLogsFilters: React.FC<AuditLogsFiltersProps> = ({ onDashboard, onExport }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const { filters, moduleOptions } = useSelector((state: any) => state.AuditLogs);

  // Local state for search input (for debounce)
  const [searchValue, setSearchValue] = useState(filters.searchTerm);

  // Từ ngày/Đến ngày nhập tay (mục UAT-ADM-036) — khác với 4 nút lọc nhanh cố định bên dưới, cặp
  // input này chỉ áp dụng filter khi bấm nút "Tìm kiếm", và validate Từ ngày <= Đến ngày trước khi
  // áp dụng (dateFormat "Y-m-d" nên so sánh chuỗi trực tiếp là đủ, không cần parse Date).
  const [fromDateInput, setFromDateInput] = useState<string>('');
  const [toDateInput, setToDateInput] = useState<string>('');

  const handleDateRangeSearch = () => {
    if (fromDateInput && toDateInput && fromDateInput > toDateInput) {
      toast.error(st('tu_ngay_phai_nho_hon_hoac_message'));
      return;
    }

    dispatch(setFilters({
      fromDate: fromDateInput || undefined,
      toDate: toDateInput || undefined,
    }));
  };

  // Debounce search input - update Redux after 500ms. Bỏ qua khi searchValue chưa thực sự đổi so với
  // filters.searchTerm hiện tại (VD: lần chạy đầu tiên lúc mount) — nếu không, dispatch setFilters vẫn
  // tạo object filters mới dù giá trị giống hệt, khiến effect load list ở trang cha (phụ thuộc filters)
  // chạy thêm 1 lần nữa ~500ms sau khi mount, gọi API list 2 lần cho cùng 1 lượt vào trang.
  useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timer = setTimeout(() => {
      dispatch(setFilters({ searchTerm: searchValue }));
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, dispatch]);

  // Nạp danh sách Module thực tế (distinct từ AuditLog) 1 lần khi mount, đổ vào dropdown filter
  // "Module" thay vì hardcode 1 danh sách cố định không khớp dữ liệu thật.
  useEffect(() => {
    dispatch(fetchAuditLogModules());
  }, [dispatch]);
  
  // Sync local state with Redux when filters are cleared
  useEffect(() => {
    if (filters.searchTerm === '') {
      setSearchValue('');
    }
  }, [filters.searchTerm]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  
  const handleActionTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ actionType: e.target.value || undefined }));
  };
  
  const handleModuleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ module: e.target.value || undefined }));
  };
  
  const handleResultChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ result: e.target.value || undefined }));
  };
  
  const handleClearFilters = () => {
    setSearchValue('');
    setFromDateInput('');
    setToDateInput('');
    dispatch(clearFilters());
  };
  
  // Quick date filter handlers
  const handleQuickFilter = useCallback((type: 'today' | '7days' | '30days' | 'thismonth') => {
    const now = new Date();
    let fromDate: Date;
    let toDate = now;
    
    switch (type) {
      case 'today':
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      case '7days':
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'thismonth':
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        break;
      default:
        fromDate = now;
    }
    
    dispatch(setFilters({ 
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString()
    }));
  }, [dispatch]);
  
    return (
      <div className="mb-3">
        <Row className="g-3 align-items-end">
          {/* Search Box */}
          <Col md="auto">
            <Label className="form-label">{st('tim_kiem_label')}</Label>
            <Input 
              type="text"
              placeholder={st('tim_kiem_placeholder')}
              value={searchValue}
              onChange={handleSearchChange}
              className="form-control"
              style={{ width: '229px' }}
            />
            {/* {searchValue && (
              <small className="text-muted">Searching...</small>
            )} */}
          </Col>

          {/* Khoảng thời gian tìm kiếm nhập tay (mục UAT-ADM-036) — chỉ áp dụng khi bấm "Tìm kiếm",
              không tự động lọc theo từng lần đổi ngày như 2 dropdown bên dưới. */}
          <Col md="auto">
            <Label className="form-label">{st('tu_ngay_label')}</Label>
            <Flatpickr
              className="form-control"
              placeholder={st('dd_mm_yyyy_placeholder')}
              style={{ width: '150px' }}
              value={fromDateInput || undefined}
              options={{
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd/m/Y',
                locale: Vietnamese,
              }}
              onChange={(_dates, dateStr) => setFromDateInput(dateStr)}
            />
          </Col>
          <Col md="auto">
            <Label className="form-label">{st('den_ngay_label')}</Label>
            <Flatpickr
              className="form-control"
              placeholder={st('dd_mm_yyyy_placeholder')}
              style={{ width: '150px' }}
              value={toDateInput || undefined}
              options={{
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd/m/Y',
                locale: Vietnamese,
              }}
              onChange={(_dates, dateStr) => setToDateInput(dateStr)}
            />
          </Col>
          <Col md="auto">
            <button
              className="btn btn-primary"
              onClick={handleDateRangeSearch}
            >
              <i className="ri-search-line me-1"></i>
              {st('tim_kiem_label')}
            </button>
          </Col>

          {/* Action Filter - map theo ActionType cố định (Created/Updated/Deleted/Exported),
              không phải Action chi tiết (tự do, hàng chục giá trị khác nhau) */}
          <Col md="auto">
            <Label className="form-label">{st('hanh_dong_label')}</Label>
            <Input
              type="select"
              value={filters.actionType || ''}
              onChange={handleActionTypeChange}
              className="form-select"
              style={{ width: '229px' }}
            >
              <option value="">{st('tat_ca_option')}</option>
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
          </Col>

          {/* Module Filter - nạp danh sách module thực tế (distinct) từ AuditLog qua API,
              không hardcode */}
          <Col md="auto">
            <Label className="form-label">{st('module_label')}</Label>
            <Input
              type="select"
              value={filters.module || ''}
              onChange={handleModuleChange}
              className="form-select"
              style={{ width: '229px' }}
            >
              <option value="">{st('tat_ca_option')}</option>
              {moduleOptions.map((m: string) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Input>
          </Col>
        
          {/* Result Filter */}
          <Col md="auto">
            <Label className="form-label">{st('ket_qua_label')}</Label>
            <Input 
              type="select"
              value={filters.result || ''}
              onChange={handleResultChange}
              className="form-select"
              style={{ width: '229px' }}
            >
              <option value="">{st('tat_ca_option')}</option>
              <option value="Success">{st('thanh_cong_option')}</option>
              <option value="Failed">{st('that_bai_option')}</option>
              <option value="Warning">{st('canh_bao_option')}</option>
            </Input>
          </Col>
        
                {/* Clear Filters Button */}
                <Col md="auto">
                  <button 
                    className="btn btn-soft-secondary"
                    onClick={handleClearFilters}
                  >
                    <i className="ri-close-circle-line me-1"></i>
                    {st('xoa_bo_loc_button')}
                  </button>
                </Col>
        
                {/* Bên phải: Các nút actions */}
                <Col md="auto" className="ms-auto">
                  <div className="d-flex gap-2">
                    {/*<button 
                      className="btn btn-info"
                      onClick={onDashboard}
                    >
                      <i className="ri-dashboard-line me-1"></i>
                      Trang tổng quan
                    </button>*/}
                    {PageActionPermission.canDisplay(location.pathname, "button-export") && (
                      <button
                        className="btn btn-success"
                        data-action-code="button-export"
                        data-action-name="Xuất dữ liệu nhật ký"
                        onClick={onExport}
                      >
                        <i className="ri-download-line me-1"></i>
                        {st('xuat_du_lieu_button')}
                      </button>
                    )}
                  </div>
                </Col>
      </Row>
      
      {/* Quick Filter Buttons */}
      <Row className="mt-2">
        <Col>
          <div className="d-flex gap-2 flex-wrap">
            <button 
              className="btn btn-sm btn-soft-primary"
              onClick={() => handleQuickFilter('today')}
            >
              <i className="ri-calendar-line me-1"></i>
              {st('hom_nay_button')}
            </button>
            <button 
              className="btn btn-sm btn-soft-primary"
              onClick={() => handleQuickFilter('7days')}
            >
              <i className="ri-calendar-line me-1"></i>
              {st('7_ngay_button')}
            </button>
            <button 
              className="btn btn-sm btn-soft-primary"
              onClick={() => handleQuickFilter('30days')}
            >
              <i className="ri-calendar-line me-1"></i>
              {st('30_ngay_button')}
            </button>
            <button 
              className="btn btn-sm btn-soft-primary"
              onClick={() => handleQuickFilter('thismonth')}
            >
              <i className="ri-calendar-line me-1"></i>
              {st('thang_nay_button')}
            </button>
            
            {/* Show active date filter indicator */}
            {(filters.fromDate || filters.toDate) && (
              <span className="badge bg-info align-self-center">
                <i className="ri-filter-line me-1"></i>
                {st('bo_loc_ngay_dang_hoat_dong_text')}
              </span>
            )}
          </div>
        </Col>
      </Row>
      
      {/* Active Filters Summary */}
      {(filters.actionType || filters.module || filters.result || searchValue) && (
        <Row className="mt-2">
          <Col>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <small className="text-muted">{st('bo_loc_dang_hoat_dong_hint')}</small>
              {filters.actionType && (
                <span className="badge bg-primary">
                  {st('hanh_dong_text')} {ACTION_TYPE_OPTIONS.find((o) => o.value === filters.actionType)?.label || filters.actionType}
                </span>
              )}
              {filters.module && (
                <span className="badge bg-primary">
                  {st('module_text')} {filters.module}
                </span>
              )}
              {filters.result && (
                <span className="badge bg-primary">
                  {st('ket_qua_text')} {filters.result}
                </span>
              )}
              {searchValue && (
                <span className="badge bg-primary">
                  {st('tim_kiem_text')}{searchValue}"
                </span>
              )}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default AuditLogsFilters;